import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * One tick-box filter axis.
 *
 * Decision, Action and Channel are the same control three times over, so it is written once. Empty
 * means "everything", exactly as the channel filter on the rules page treats it: a brand that has
 * ticked nothing is not asking to see nothing.
 */

export interface ActionLogFilterOption {
	value: string;
	label: string;
	/** Optional trailing note — the sentence explaining what a decision actually means. */
	hint?: string;
	icon?: ReactNode;
}

interface ActionLogMultiSelectProps {
	label: string;
	options: ActionLogFilterOption[];
	value: string[];
	onChange: (next: string[]) => void;
	/** Shown in place of the list when there is nothing to pick from. */
	emptyMessage?: string;
	className?: string;
}

const toggle = (list: string[], item: string): string[] =>
	list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];

export const ActionLogMultiSelect = ({
	label,
	options,
	value,
	onChange,
	emptyMessage = 'Nothing to filter by yet',
	className
}: ActionLogMultiSelectProps) => {
	const isActive = value.length > 0;

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant='outline'
					// Figma 4768:15329 — 28px, a gray-500 hairline, no shadow, label and chevron both
					// in the headings colour. The count badge is not in the design and stays anyway:
					// an active filter has to be legible with the popover CLOSED, or a list narrowed
					// to two decisions is indistinguishable from an empty account.
					className={cn(
						'h-7 gap-2 rounded-full border-gray-500 pl-3 pr-2.5 text-[13px] leading-[18px] font-normal text-ink-strong shadow-none',
						isActive && 'border-ink-strong font-medium',
						className
					)}
					aria-label={`${label} filter`}
				>
					<span className='flex items-center gap-1.5'>
						{label}
						{isActive && (
							<span className='rounded-full bg-foreground px-1.5 text-[11px] leading-4 text-primary-foreground'>
								{value.length}
							</span>
						)}
					</span>
					<ChevronDown className='size-3.5' />
				</Button>
			</PopoverTrigger>

			<PopoverContent align='start' className='w-[320px] rounded-2xl p-3'>
				<div className='flex items-center justify-between px-2 pb-1'>
					<p className='text-xs text-muted-foreground'>{label}</p>
					{isActive && (
						<button
							type='button'
							className='text-xs text-muted-foreground underline-offset-2 hover:underline'
							onClick={() => onChange([])}
						>
							Clear
						</button>
					)}
				</div>

				{options.length === 0 ? (
					<p className='px-2 py-2 text-[13px] text-muted-foreground'>{emptyMessage}</p>
				) : (
					options.map((option) => (
						<label
							key={option.value}
							className='flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 text-[13px] text-foreground hover:bg-muted'
						>
							<Checkbox
								className='mt-0.5'
								checked={value.includes(option.value)}
								onCheckedChange={() => onChange(toggle(value, option.value))}
							/>
							{option.icon}
							<span className='min-w-0 flex-1'>
								<span className='block truncate'>{option.label}</span>
								{option.hint && <span className='block text-xs text-muted-foreground'>{option.hint}</span>}
							</span>
						</label>
					))
				)}
			</PopoverContent>
		</Popover>
	);
};
